import * as LabelPrimitive from "@radix-ui/react-label";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  Children,
  forwardRef,
  isValidElement,
  useId,
  useMemo,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { Icon } from "./icon";
import { cn } from "@/lib/cn";

/**
 * Form contract for the whole app: label ABOVE the control, helper under the
 * label, error under the control in the destructive colour. Placeholders are
 * examples, never labels.
 */
interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, hint, error, required, children, className }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-col gap-1">
        <LabelPrimitive.Root
          htmlFor={htmlFor}
          className="text-[13px] font-medium leading-none text-ink"
        >
          {label}
          {required && (
            <span className="ml-1 text-bad" aria-hidden>
              *
            </span>
          )}
        </LabelPrimitive.Root>
        {hint && <p className="text-xs leading-normal text-muted">{hint}</p>}
      </div>
      {children}
      {error && (
        <p className="flex items-start gap-1.5 text-xs leading-normal text-bad" role="alert">
          <Icon name="warning-circle" size={16} className="mt-px" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

const controlBase =
  "w-full rounded-lg border bg-surface px-3 text-sm text-ink transition-colors " +
  "hover:border-line-strong focus:border-accent disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-faint";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...props }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(controlBase, "h-9", invalid && "border-bad", className)}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(controlBase, "min-h-20 py-2 leading-relaxed", invalid && "border-bad", className)}
      {...props}
    />
  );
});

/**
 * Radix rejects "" as an item value, so the native "no selection" option is
 * carried through the popup under a sentinel and mapped back on the way out.
 */
const EMPTY_VALUE = " none";
const encode = (value: string) => (value === "" ? EMPTY_VALUE : value);
const decode = (value: string) => (value === EMPTY_VALUE ? "" : value);

interface OptionSpec {
  value: string;
  label: ReactNode;
  disabled: boolean;
}

/** Reads the <option> children so call sites keep the native authoring shape. */
function readOptions(children: ReactNode): OptionSpec[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child) || child.type !== "option") return [];
    const props = child.props as {
      value?: string | number;
      children?: ReactNode;
      disabled?: boolean;
    };
    return [
      {
        value: String(props.value ?? ""),
        label: props.children,
        disabled: Boolean(props.disabled),
      },
    ];
  });
}

export interface SelectChangeEvent {
  target: { value: string; id: string | undefined; name: string | undefined };
}

interface SelectProps {
  id?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  /** Shaped like a native change event so feature code reads e.target.value. */
  onChange?: (event: SelectChangeEvent) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  placeholder?: string;
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

/**
 * The popup is a Radix portal, not the browser's option list. A native list
 * cannot be themed — it paints a system-white sheet with a system-blue
 * highlight, which reads as foreign against the warm paper palette and does
 * not follow the dark theme. The trigger keeps the shared control shape, and
 * the call surface stays native: pass <option> children, read e.target.value.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    id,
    name,
    value,
    defaultValue,
    onChange,
    disabled,
    required,
    invalid,
    placeholder,
    className,
    children,
    ...aria
  },
  ref,
) {
  const options = useMemo(() => readOptions(children), [children]);

  return (
    <SelectPrimitive.Root
      value={value === undefined ? undefined : encode(value)}
      defaultValue={defaultValue === undefined ? undefined : encode(defaultValue)}
      onValueChange={(next) => onChange?.({ target: { value: decode(next), id, name } })}
      disabled={disabled}
      required={required}
      name={name}
    >
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        aria-invalid={invalid || undefined}
        className={cn(
          controlBase,
          "flex h-9 items-center justify-between gap-2 text-left",
          "data-[state=open]:border-accent data-[placeholder]:text-faint",
          invalid && "border-bad",
          className,
        )}
        {...aria}
      >
        <span className="min-w-0 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <Icon name="caret-down" size={16} className="text-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            "z-50 overflow-hidden rounded-lg border border-line bg-surface-raised shadow-pop",
            "min-w-[var(--radix-select-trigger-width)]",
            "max-h-[var(--radix-select-content-available-height)]",
            "origin-[var(--radix-select-content-transform-origin)] data-[state=open]:animate-pop-in",
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center text-muted">
            <Icon name="caret-up" size={16} />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="max-h-72 p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={encode(option.value)}
                disabled={option.disabled}
                className={cn(
                  "relative flex cursor-pointer select-none items-center rounded-md py-1.5 pl-2 pr-8",
                  "text-sm leading-tight text-ink outline-none transition-colors",
                  "data-[highlighted]:bg-accent-wash data-[highlighted]:text-accent",
                  "data-[state=checked]:font-medium",
                  "data-[disabled]:pointer-events-none data-[disabled]:text-faint",
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-2 flex items-center text-accent">
                  <Icon name="check" size={16} />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center text-muted">
            <Icon name="caret-down" size={16} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});

/**
 * Convenience for the common "one label, one input" case. A password field
 * (type="password") automatically gets a show/hide toggle — every password
 * input in the app goes through this component, so the toggle lands
 * everywhere at once rather than being wired up per page.
 */
export function LabelledInput({
  label,
  hint,
  error,
  required,
  className,
  type,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string | null;
  className?: string;
}) {
  const generated = useId();
  const id = props.id ?? generated;
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === "password";

  return (
    <Field
      label={label}
      htmlFor={id}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      {...(required ? { required } : {})}
      {...(className ? { className } : {})}
    >
      {isPassword ? (
        <div className="relative">
          <Input
            id={id}
            type={revealed ? "text" : "password"}
            invalid={Boolean(error)}
            required={required}
            className="pr-9"
            {...props}
          />
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? "Hide password" : "Show password"}
            className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center justify-center text-muted transition-colors hover:text-ink"
          >
            <Icon name={revealed ? "eye-slash" : "view"} size={16} />
          </button>
        </div>
      ) : (
        <Input id={id} type={type} invalid={Boolean(error)} required={required} {...props} />
      )}
    </Field>
  );
}
